import React, { useState } from 'react';
import { useMobileDetection } from '../hooks/useMobileDetection';
import LogoNav from '../components/Navigation/LogoNav';
import '../App.css';

const Legal: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'terms' | 'privacy'>('terms');
  const { isMobile, isTablet } = useMobileDetection();
  
  // Treat tablets as mobile for layout purposes
  const useMobileLayout = isMobile || isTablet;

  return (
    <div className="legal-page" style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f8fafc', 
      paddingTop: useMobileLayout ? '60px' : '80px',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif'
    }}>
      {!useMobileLayout && <LogoNav />}
      <div style={{ 
        maxWidth: '800px', 
        margin: '0 auto', 
        padding: '40px 20px'
      }}>
        <div style={{ 
          backgroundColor: 'white',
          padding: '48px',
          borderRadius: '16px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
          border: '1px solid #f1f5f9'
        }}>
          <div style={{ 
            textAlign: 'center',
            marginBottom: '40px' 
          }}>
            <h1 style={{ 
              fontSize: '32px', 
              fontWeight: '600', 
              color: '#1f2937',
              margin: 0
            }}>
              ZoningPal Legal
            </h1>
          </div>
        
        {/* Tab Navigation - Segmented Control */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '30px'
        }}>
          <div style={{
            display: 'flex',
            backgroundColor: '#f1f5f9',
            borderRadius: '12px',
            padding: '4px',
            gap: '2px',
            width: useMobileLayout ? '100%' : 'auto'
          }}>
            <button
              onClick={() => setActiveTab('terms')}
              style={{
                padding: useMobileLayout ? '14px 16px' : '12px 24px',
                fontSize: '15px',
                fontWeight: '500',
                backgroundColor: activeTab === 'terms' ? '#ffffff' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                color: activeTab === 'terms' ? '#1f2937' : '#6b7280',
                transition: 'all 0.2s ease',
                boxShadow: activeTab === 'terms' ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)' : 'none',
                flex: useMobileLayout ? '1' : 'none',
                minWidth: useMobileLayout ? 'auto' : '140px'
              }}
            >
              Terms of Service
            </button>
            <button
              onClick={() => setActiveTab('privacy')}
              style={{
                padding: useMobileLayout ? '14px 16px' : '12px 24px',
                fontSize: '15px',
                fontWeight: '500',
                backgroundColor: activeTab === 'privacy' ? '#ffffff' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                color: activeTab === 'privacy' ? '#1f2937' : '#6b7280',
                transition: 'all 0.2s ease',
                boxShadow: activeTab === 'privacy' ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)' : 'none',
                flex: useMobileLayout ? '1' : 'none',
                minWidth: useMobileLayout ? 'auto' : '140px'
              }}
            >
              Privacy Policy
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ lineHeight: '1.6', color: '#374151' }}>
          {activeTab === 'terms' ? (
            <>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '16px',
                marginTop: '0'
              }}>
                Terms of Service
              </h2>
              <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '24px' }}>
                (Last updated 14 Jul 2025)
              </p>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                1. Acceptance of These Terms
              </h3>
              <p style={{ marginBottom: '16px' }}>
                By creating an account, purchasing credits or a subscription, downloading a zoning brief,
                or otherwise using the ZoningPal website, API, or related services ("Service"), you
                ("User" or "you") agree to be bound by:
              </p>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li>these Terms of Service ("Terms"); and</li>
                <li>any order forms or additional guidelines we post.</li>
              </ul>
              <p style={{ marginBottom: '16px' }}>
                If you are accepting on behalf of a company, you represent that you have authority to
                bind that company. If you do not agree, do not use the Service.
              </p>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                2. Who We Are
              </h3>
              <p style={{ marginBottom: '16px' }}>
                The Service is operated by Stoop Inc. (Ontario Corporation No. 1000660122; Federal
                Corporation No. 1536786-7), 301 Pleasant Avenue, Toronto ON M2R 2R2 Canada ("Stoop
                Inc.," "we," "our").
              </p>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                3. What We Provide — "Type D Informational Zoning Briefs"
              </h3>
              <p style={{ marginBottom: '16px' }}>
                ZoningPal delivers automated, data-driven briefing documents that consolidate publicly
                available and proprietary datasets about a parcel's zoning context. The briefs are for
                preliminary due-diligence only and are not legal advice, an Applicable-Law Certificate,
                Zoning Waiver, or professional opinion under Ontario's Building Code, Planning Act, or
                any other legislation. Always verify with Toronto Building and a qualified professional
                before purchase, design, or construction.
              </p>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                4. User Accounts and Credits
              </h3>
              <p style={{ marginBottom: '16px' }}>
                Access to zoning briefs requires an account and credits. Credits can be purchased
                individually or through subscription plans. Account information must be accurate and
                current. You are responsible for maintaining account security.
              </p>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                5. Payment and Billing
              </h3>
              <p style={{ marginBottom: '16px' }}>
                All prices are in Canadian dollars (CAD). Subscription plans automatically renew unless
                cancelled. Credits do not expire. Refunds are provided according to our refund policy.
                Payment processing is handled securely through Stripe.
              </p>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                6. Acceptable Use
              </h3>
              <p style={{ marginBottom: '16px' }}>
                You may use the Service for lawful purposes only. You may not:
              </p>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li>Share account credentials or resell access</li>
                <li>Attempt to reverse engineer or scrape the Service</li>
                <li>Use the Service for any illegal or harmful purposes</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                7. Limitations and Disclaimers
              </h3>
              <p style={{ marginBottom: '16px' }}>
                ZoningPal briefs are informational tools based on available data. We make no guarantees
                about accuracy, completeness, or currency of information. The Service is provided "as is"
                without warranties. We are not liable for decisions made based on our briefs.
              </p>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                8. Privacy and Data
              </h3>
              <p style={{ marginBottom: '16px' }}>
                Your privacy is important to us. Please review our Privacy Policy to understand how we
                collect, use, and protect your information.
              </p>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                9. Changes to Terms
              </h3>
              <p style={{ marginBottom: '16px' }}>
                We may update these Terms from time to time. Continued use of the Service after changes
                constitutes acceptance of the new Terms. We will notify users of material changes.
              </p>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                10. Contact Information
              </h3>
              <p style={{ marginBottom: '16px' }}>
                For questions about these Terms, please contact us at admin@zoningpal.com or 301 Pleasant
                Avenue, Toronto ON M2R 2R2 Canada.
              </p>
            </>
          ) : (
            <>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '16px',
                marginTop: '0'
              }}>
                Privacy Policy
              </h2>
              <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '24px' }}>
                (Last updated 14 Jul 2025)
              </p>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                1. Information We Collect
              </h3>
              <p style={{ marginBottom: '16px' }}>
                We collect information you provide directly to us, such as when you create an account,
                make a purchase, or contact us. This includes:
              </p>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li>Account information (email address, name)</li>
                <li>Payment information (processed securely by Stripe)</li>
                <li>Property addresses you search for</li>
                <li>Communication with our support team</li>
              </ul>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                2. How We Use Your Information
              </h3>
              <p style={{ marginBottom: '16px' }}>
                We use the information we collect to:
              </p>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li>Provide and improve our services</li>
                <li>Process payments and manage subscriptions</li>
                <li>Send service-related communications</li>
                <li>Respond to your questions and provide support</li>
                <li>Comply with legal obligations</li>
              </ul>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                3. Information Sharing
              </h3>
              <p style={{ marginBottom: '16px' }}>
                We do not sell, trade, or otherwise transfer your personal information to third parties
                except:
              </p>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li>To service providers who help us operate our business</li>
                <li>When required by law or to protect our rights</li>
                <li>In connection with a business transfer or merger</li>
              </ul>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                4. Data Security
              </h3>
              <p style={{ marginBottom: '16px' }}>
                We implement appropriate security measures to protect your personal information against
                unauthorized access, alteration, disclosure, or destruction. However, no method of
                transmission over the internet is 100% secure.
              </p>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                5. Your Rights
              </h3>
              <p style={{ marginBottom: '16px' }}>
                You have the right to:
              </p>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li>Access and update your account information</li>
                <li>Request deletion of your personal data</li>
                <li>Withdraw consent for data processing</li>
                <li>File a complaint with relevant authorities</li>
              </ul>

              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                marginBottom: '12px',
                marginTop: '32px'
              }}>
                6. Contact Us
              </h3>
              <p style={{ marginBottom: '16px' }}>
                If you have questions about this Privacy Policy, please contact us at admin@zoningpal.com.
              </p>
            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default Legal; 